import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import "@/i18n";
import App from "@/App";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const app = (
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>
);
// Prerendered routes (scripts/browser_prerender.js) arrive with their content in #root.
// Hydrating them fails wherever a page fetches data after mount (React's first draw shows
// "Loading…" where the snapshot has content), and React then redraws the page blank. So the
// snapshot stays on screen while the live app renders beside it, hidden, and replaces it once
// it has drawn the same <h1> (or after a short timeout). The 404.html SPA shell has an empty
// root and renders directly.
const SWAP_TIMEOUT_MS = 3000;
const container = document.getElementById("root");
if (!container.hasChildNodes()) {
  ReactDOM.createRoot(container).render(app);
} else {
  const live = document.createElement("div");
  live.setAttribute("aria-hidden", "true");
  live.style.cssText = "position:absolute;top:0;left:0;width:100%;visibility:hidden;pointer-events:none";
  container.after(live);
  const heading = (el) => (el.querySelector("main h1")?.textContent || "").trim();
  const target = heading(container);
  let done = false;
  let observer;
  let timer;
  const swap = () => {
    if (done) return;
    done = true;
    observer.disconnect();
    clearTimeout(timer);
    container.remove();
    live.removeAttribute("aria-hidden");
    live.removeAttribute("style");
    live.id = "root";
  };
  const check = () => {
    if (!live.querySelector("main") || live.querySelector('[data-testid="route-loading"]')) return;
    if (!target || heading(live) === target) swap();
  };
  observer = new MutationObserver(check);
  observer.observe(live, { childList: true, subtree: true, characterData: true });
  timer = setTimeout(swap, SWAP_TIMEOUT_MS);
  ReactDOM.createRoot(live).render(app);
}
